/**
 * The FTUX's transitions (blueprint section 4) and choke point C1 of its events: each thunk checks it applies, moves
 * the run on screen, writes the owner's records when it moved, and reports the moment through ftuxEvent. A run moves,
 * and its records are written, only while the owner's preferences can be trusted and the run on screen is the
 * displayed brand's (mayWriteFtux); otherwise the thunk does nothing at all, so the tab never holds a run the owner's
 * records do not.
 *
 * Records: the owner's intro (Lumio's hello and the map) under PREFERENCE_KEYS.ftuxIntro, and each brand's run under
 * FTUX_RUN_KEY_PREFIX + its brand id. A write sends only the records the transition moved, as they stood then, so a
 * tab never writes a record it did not move (one record moved in two tabs is still the last write's); a dropped run is
 * tombstoned by the QA control that drops it.
 *
 * Event order: the trigger first (the click), then the piece's `ended`, then each piece passed by on the way
 * (`ended skipped` at `skip:<reason>`), then `finished` when the run just ended. QA events carry the new run.
 */
import type { AppDispatch, RootState } from '@/store';
import { FTUX_RUN_KEY_PREFIX, PREFERENCE_KEYS, saveUserPreferences } from '@/store/slices/userPreferences';

import {
  mapStep,
  skipStep,
  type BrandPieceId,
  type FtuxFlowStep,
  type FtuxOutcome,
  type FtuxPieceStep,
  type FtuxStep,
  type SkipReason,
} from '../catalog';
import type { FtuxTargetId } from '../targets';
import { ftuxEvent, type FtuxThunk } from './ftuxEvent';
import {
  hydrated,
  missedNoted,
  phaseSet,
  progressSet,
  runDropped,
  runsCleared,
  saveDone,
  saveFailed,
  savePending,
  saveStarted,
  skipsNoted,
} from './ftuxSlice';
import { freshStamp, mayWriteFtux, prefsReliableFor, readFtuxRecords, toStoredPatch } from './persistence';
import { progressAfter, reconcileWaiting, withOutcome, type ProgressStep } from './progression';
import type {
  FtuxIntroExit,
  FtuxPatch,
  FtuxPieceExit,
  FtuxProgress,
  FtuxQaTarget,
  FtuxStatus,
  FtuxStepExit,
  PieceOutcome,
} from './types';

/** The English words of the controls the store reports itself (the components report their own). */
const DISMISS_LABEL = 'Dismiss';
/** A step's close button (the pop-up's, the map's). */
const CLOSE_LABEL = 'Close';
const INTRO_EXIT_LABELS: Readonly<Record<FtuxIntroExit, string | undefined>> = {
  'not-now': 'Not now',
  close: CLOSE_LABEL,
};

/**
 * The words a way out of a step logs with its `clicked` event: its close button's, and none for Escape (a key has no
 * copy), as the pop-up logs them. Shared with the map, which reports its own clicks.
 */
export const stepExitLabel = (via: FtuxStepExit): string | undefined => (via === 'close' ? CLOSE_LABEL : undefined);

/* The single writer of the owner's records */

/** Counts this tab's writes: a write that lands after the slice was reset and written again leaves it alone. */
let writeTicket = 0;
/** The last record's updatedAt, so each one is later than the last (no two bodies match the axios POST cache). */
let lastStamp: string | null = null;
/** Counts this tab's reads of the owner's records (the slice's readId). */
let readCount = 0;
/** The next try of a failed write (null: none waiting). Global timers: the store runs in node's tests too. */
let retryTimer: ReturnType<typeof setTimeout> | null = null;
/** The tries made since the owner's last transition or the last write that landed. */
let retryCount = 0;

const FTUX_SAVE_FAILED = 'The FTUX progress was not saved.';
/**
 * A failed write is sent again after this long (ms), each try twice as late as the last, up to SAVE_RETRY_MAX tries:
 * the run's last transition (its last piece answered) has no next one to carry its keys. A write that keeps failing
 * stops being sent; its keys wait for the owner's next transition.
 */
const SAVE_RETRY_MS = 15_000;
const SAVE_RETRY_MAX = 4;

const canWrite = (state: RootState): boolean =>
  mayWriteFtux({
    mounted: state.ftux.mounted,
    hydrated: state.ftux.hydrated,
    newer: state.ftux.newer,
    sliceUserId: state.ftux.userId,
    userId: state.authentication.user.user_id,
    sliceBrandId: state.ftux.brandId,
    brandId: state.casinoInstance.displayBrand?.id ?? null,
    impersonated: Boolean(state.authentication.user.admin),
    prefs: state.userPreferences,
  });

/** A brand's run key. */
const runKeyOf = (brandId: string): string => `${FTUX_RUN_KEY_PREFIX}${brandId}`;

/** Which records a transition moved: the owner's intro, the run of the brand on screen, or both. */
type Moved = { intro?: boolean; run?: boolean };

const INTRO: Moved = { intro: true };
const RUN: Moved = { run: true };
const INTRO_AND_RUN: Moved = { intro: true, run: true };

/**
 * The records a transition moved, as they stand now, and only those (another tab's records are never written over
 * with what this tab did not change): the owner's intro, and the run of the brand on screen. Taken when the run
 * moves, so a write that waits keeps a run under its own brand's key. A dropped run is tombstoned by the transition
 * that drops it, never here.
 */
const patchOf = (state: RootState, moved: Moved): FtuxPatch => {
  const { intro, runs, brandId } = state.ftux;
  const patch: Record<string, FtuxPatch[string]> = {};
  if (moved.intro && intro !== null) patch[PREFERENCE_KEYS.ftuxIntro] = { kind: 'intro', intro };
  const run = brandId !== null ? runs[brandId] : undefined;
  if (moved.run && brandId !== null && run) patch[runKeyOf(brandId)] = { kind: 'run', progress: run };
  return patch;
};

/**
 * The next try of the keys a failed write left waiting, unless the tries are spent. A try that finds them gone (written,
 * or the owner's part reset) or a write in flight (it retries on its own) ends there; one the owner may not write now
 * (a brand switching, the preferences read again) waits for the next.
 */
const retryLater = (dispatch: AppDispatch, getState: () => RootState): void => {
  if (retryCount >= SAVE_RETRY_MAX) return;
  const delay = SAVE_RETRY_MS * 2 ** retryCount;
  retryCount += 1;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    const later = getState();
    if (Object.keys(later.ftux.save.pending).length === 0 || later.ftux.save.inFlight) return;
    if (canWrite(later)) void writePending(dispatch, getState);
    else retryLater(dispatch, getState);
  }, delay);
};

/**
 * POSTs every pending key, then the ones that came meanwhile. A failed POST's keys wait for the next write: the next
 * transition's, or a try of their own (retryLater) while the owner may still write.
 */
const writePending = async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
  const { pending } = getState().ftux.save;
  if (Object.keys(pending).length === 0) return;
  if (retryTimer !== null) clearTimeout(retryTimer);
  retryTimer = null;
  writeTicket += 1;
  const ticket = writeTicket;
  // The read these records came from: an answer that lands after another read (another owner, or the same one read
  // again) leaves it alone.
  const read = getState().ftux.readId;
  lastStamp = freshStamp(Date.now(), lastStamp);
  dispatch(saveStarted());
  // Each record whole: the backend merges top-level keys shallowly.
  let failure: string | null = null;
  try {
    const result = await dispatch(saveUserPreferences(toStoredPatch(pending, lastStamp)));
    if (!saveUserPreferences.fulfilled.match(result)) {
      failure = result.payload?.error ?? result.error.message ?? FTUX_SAVE_FAILED;
    }
  } catch (error: unknown) {
    failure = error instanceof Error ? error.message : FTUX_SAVE_FAILED;
  }
  const now = getState().ftux;
  if (ticket !== writeTicket || !now.hydrated || now.readId !== read) return;
  dispatch(failure === null ? saveDone() : saveFailed({ error: failure, patch: pending }));
  if (failure !== null) {
    retryLater(dispatch, getState);
    return;
  }
  retryCount = 0;
  if (Object.keys(getState().ftux.save.pending).length > 0) {
    await writePending(dispatch, getState);
  }
};

/**
 * Writes the records a transition `moved` (the intro, the brand's run) as they stand. One write at a time: asked while
 * one is in flight, its keys go with the next one. A failure stays in save.error, its keys waiting for the next
 * transition or the write's own retry.
 */
const persistFtux =
  (moved: Moved): FtuxThunk =>
  (dispatch, getState) => {
    const state = getState();
    if (!canWrite(state)) return;
    dispatch(savePending(patchOf(state, moved)));
    // The owner moved: a write of theirs failing again gets its tries afresh.
    retryCount = 0;
    if (state.ftux.save.inFlight) return;
    void writePending(dispatch, getState);
  };

/* Moving the run */

/** Where the run goes from `from` over the pieces this build ships; null until FtuxRoot has published them. */
const advance = (state: RootState, progress: FtuxProgress, from: BrandPieceId | null): ProgressStep | null => {
  const { shipped, availability } = state.ftux;
  if (!shipped) return null;
  const ids = shipped.map((meta) => meta.id);
  return progressAfter(progress, from, ids, availability);
};

/**
 * The run moved: its progress, why pieces were passed by, the write (the brand's run, and the intro when `moved`
 * says so), then their events and finished.
 */
const commit = (dispatch: AppDispatch, before: FtuxStatus, step: ProgressStep, moved: Moved = RUN): void => {
  dispatch(progressSet(step.progress));
  if (step.skipped.length > 0) dispatch(skipsNoted(step.skipped));
  dispatch(persistFtux(moved));
  step.skipped.forEach(({ id, reason }) =>
    dispatch(ftuxEvent({ type: 'ended', piece: id, step: skipStep(reason), outcome: 'skipped' })),
  );
  if (step.progress.status === 'finished' && before !== 'finished') {
    dispatch(ftuxEvent({ type: 'finished', piece: null, step: null }));
  }
};

/** A run that starts afresh at `status`: nothing ended, nothing deferred, and never the same `run` as before. */
const freshRun = (previous: FtuxProgress | null, status: FtuxStatus, piece: BrandPieceId | null): FtuxProgress => ({
  run: Math.max(Date.now(), (previous?.run ?? 0) + 1),
  status,
  piece,
  outcomes: {},
  deferred: [],
});

/* The run's start: the hello and the map, then each brand's own */

/**
 * Reads the owner's intro and every brand's run from their preferences, once they loaded reliably for the owner
 * signed in now; the displayed brand's comes on screen.
 */
export const ftuxHydrate = (): FtuxThunk => (dispatch, getState) => {
  const state = getState();
  const userId = state.authentication.user.user_id;
  if (!state.ftux.mounted || userId === null || !prefsReliableFor(state.userPreferences, userId)) return;
  if (state.ftux.hydrated && state.ftux.userId === userId) return;
  const records = readFtuxRecords(state.userPreferences.preferences, PREFERENCE_KEYS.ftuxIntro, FTUX_RUN_KEY_PREFIX);
  readCount += 1;
  dispatch(hydrated({ userId, brandId: state.casinoInstance.displayBrand?.id ?? null, records, readId: readCount }));
};

/**
 * A new run when the screen has none (the gate's canStart): Lumio's hello for an owner who has not started the intro;
 * for a brand with no run of its own once the intro is done, its first piece that can be offered waits on Spotlight
 * (a no-op until FtuxRoot has published the shipped pieces, then the session starts it again).
 */
export const ftuxStartRun = (): FtuxThunk => (dispatch, getState) => {
  const state = getState();
  const { intro, progress } = state.ftux;
  if (!canWrite(state) || progress !== null) return;
  if (intro === null) {
    dispatch(progressSet(freshRun(null, 'hello', null)));
    dispatch(persistFtux(INTRO));
    return;
  }
  const fresh = freshRun(null, 'waiting', null);
  const next = advance(state, fresh, null);
  if (next) commit(dispatch, fresh.status, next);
};

/** Lumio's mark landed in its place: the menu's map. */
export const ftuxHelloLanded = (): FtuxThunk => (dispatch, getState) => {
  const state = getState();
  const { progress } = state.ftux;
  if (!canWrite(state) || progress?.status !== 'hello') return;
  dispatch(progressSet({ ...progress, status: 'lap', piece: null }));
  dispatch(persistFtux(INTRO));
};

/**
 * The map's Got it: the intro is done. A brand on screen that already has a run (the intro replayed over it) goes on
 * with it, as it was. A brand with none gets its own run, after the intro's (the hello and the map are the owner's, so
 * their run is never a brand's): its first piece that can be offered waits on Spotlight, or the run is over.
 */
export const ftuxMapDone = (): FtuxThunk => (dispatch, getState) => {
  const state = getState();
  const { progress, brandId, runs } = state.ftux;
  if (!canWrite(state) || progress?.status !== 'lap') return;
  const own = brandId !== null ? runs[brandId] : undefined;
  if (own) {
    dispatch(progressSet(own));
    dispatch(persistFtux(INTRO));
    return;
  }
  const next = advance(state, freshRun(progress, 'waiting', null), null);
  if (next) commit(dispatch, progress.status, next, INTRO_AND_RUN);
};

/* Spotlight's card and the piece's pop-up */

/** The waiting piece's card, when nothing is open. */
const waitingPiece = (state: RootState): BrandPieceId | null => {
  const { progress, phase } = state.ftux;
  return state.ftux.mounted && phase === 'none' && progress?.status === 'waiting' ? progress.piece : null;
};

/** The card's button: the piece's pop-up opens. */
export const ftuxOpenIntro = (): FtuxThunk => (dispatch, getState) => {
  const state = getState();
  const piece = waitingPiece(state);
  if (piece === null) return;
  const label = state.ftux.shipped?.find((meta) => meta.id === piece)?.pick.cta;
  dispatch(ftuxEvent({ type: 'clicked', piece, step: 'spotlight-pick', control: 'cta', label }));
  dispatch(phaseSet({ phase: 'intro', active: piece }));
};

/** The card's Dismiss: the piece is left for later and the next one waits (or the run is over). */
export const ftuxDismissPick = (): FtuxThunk => (dispatch, getState) => {
  const state = getState();
  const piece = waitingPiece(state);
  const { progress } = state.ftux;
  if (piece === null || progress === null || !canWrite(state)) return;
  const next = advance(state, withOutcome(progress, piece, 'later'), piece);
  if (!next) return;
  dispatch(ftuxEvent({ type: 'clicked', piece, step: 'spotlight-pick', control: 'dismiss', label: DISMISS_LABEL }));
  dispatch(ftuxEvent({ type: 'ended', piece, step: 'spotlight-pick', outcome: 'later' }));
  commit(dispatch, progress.status, next);
};

/** The pop-up's piece, while it is open. */
const introPiece = (state: RootState): BrandPieceId | null =>
  state.ftux.mounted && state.ftux.phase === 'intro' ? state.ftux.active : null;

/** The pop-up's Not now or its close button: closed, the piece still waits on Spotlight. */
export const ftuxIntroNotNow =
  (via: FtuxIntroExit): FtuxThunk =>
  (dispatch, getState) => {
    const piece = introPiece(getState());
    if (piece === null) return;
    const label = INTRO_EXIT_LABELS[via];
    dispatch(ftuxEvent({ type: 'clicked', piece, step: 'piece-intro', control: 'not-now', label }));
    dispatch(phaseSet({ phase: 'none', active: null }));
  };

/** The pop-up's button (`label`, its English copy): the piece plays on its page. */
export const ftuxIntroStart =
  (label: string): FtuxThunk =>
  (dispatch, getState) => {
    const piece = introPiece(getState());
    if (piece === null) return;
    dispatch(ftuxEvent({ type: 'clicked', piece, step: 'piece-intro', control: 'cta', label }));
    dispatch(phaseSet({ phase: 'playing', active: piece }));
  };

/* The piece in play */

/** Ends the piece in play as `outcome` at `step`, then moves the run on from it, then shows `after`. */
const endPiece =
  (outcome: FtuxOutcome, step: FtuxStep, after: 'ack' | 'none'): FtuxThunk =>
  (dispatch, getState) => {
    const state = getState();
    const { progress, phase, active } = state.ftux;
    if (!canWrite(state) || phase !== 'playing' || active === null || progress === null) return;
    const next = advance(state, withOutcome(progress, active, outcome), active);
    if (!next) return;
    dispatch(ftuxEvent({ type: 'ended', piece: active, step, outcome }));
    commit(dispatch, progress.status, next);
    dispatch(phaseSet({ phase: after, active: after === 'ack' ? active : null }));
  };

/** The owner answered at `step` (ctx.complete): the outcome and the next piece at once, then Lumio's acknowledgment. */
export const ftuxPieceAnswered = (outcome: PieceOutcome, step: FtuxPieceStep): FtuxThunk =>
  endPiece(outcome, step, 'ack');

/** The owner goes on alone in the feature's own flow (ctx.handOff): the piece ends at once, no acknowledgment. */
export const ftuxPieceHandedOff = (outcome: PieceOutcome, step: FtuxPieceStep): FtuxThunk =>
  endPiece(outcome, step, 'none');

/**
 * The piece cannot go on now: its zone never appeared on the page (the page's read failed, or it does not offer the
 * control yet, as Mini-games' Create new with no promotion). It ends where it stands, as on Escape, and nothing is
 * written: its card waits on Spotlight again, so a failure never skips it for good.
 */
export const ftuxPieceZoneMissing = (): FtuxThunk => (dispatch, getState) => {
  const { ftux } = getState();
  if (!ftux.mounted || ftux.phase !== 'playing' || ftux.active === null) return;
  dispatch(phaseSet({ phase: 'none', active: null }));
};

/** The misses this page load has sent, by run, piece, step and target: one that lasts goes out once a run. */
const reportedMisses = new Set<string>();

/**
 * Something of the studio a step looked for was not on the page (`target`, at `step`): the tour goes on without it,
 * and the miss is reported, so the team sees what the studio changed under the FTUX (.claude/rules/ftux.md): on the
 * QA strip every time, and as an ows-uxo `missing` event once per run, piece, step and target a page load (a run
 * started again, by a restart or another owner, sends it again).
 */
export const ftuxMissing =
  (target: FtuxTargetId, step: FtuxStep | null): FtuxThunk =>
  (dispatch, getState) => {
    const { ftux } = getState();
    if (!ftux.mounted) return;
    dispatch(missedNoted(target));
    const piece = ftux.phase === 'playing' ? ftux.active : null;
    const key = `${ftux.progress?.run ?? '-'}|${piece ?? '-'}|${step ?? '-'}|${target}`;
    if (reportedMisses.has(key)) return;
    reportedMisses.add(key);
    dispatch(ftuxEvent({ type: 'missing', piece, step, target }));
  };

/** Lumio's acknowledgment ran out or was dismissed: nothing open, the next piece's card waits on Home. */
export const ftuxAckEnded = (): FtuxThunk => (dispatch, getState) => {
  if (getState().ftux.phase === 'ack') dispatch(phaseSet({ phase: 'none', active: null }));
};

/**
 * The flow's layer threw (FtuxRoot's boundary; React has reported the error): what failed is passed by, so the run
 * goes on with no reload. An open pop-up closes, a piece in play or its acknowledgment ends where it stands (its card
 * waits on Spotlight again); Lumio's hello or the map that failed moves on as if it had ended.
 */
export const ftuxLayerFailed = (): FtuxThunk => (dispatch, getState) => {
  const { ftux } = getState();
  if (!ftux.mounted) return;
  if (ftux.phase !== 'none') {
    dispatch(phaseSet({ phase: 'none', active: null }));
    return;
  }
  if (ftux.progress?.status === 'hello') dispatch(ftuxHelloLanded());
  else if (ftux.progress?.status === 'lap') dispatch(ftuxMapDone());
};

/**
 * The close in the screen's corner (its X, `via`): the owner stays where they are, the same piece waits on Spotlight.
 * Reported as `dismiss` with "Close".
 */
export const ftuxLeavePiece =
  (via: FtuxPieceExit): FtuxThunk =>
  (dispatch, getState) => {
    const { ftux } = getState();
    if (!ftux.mounted || ftux.phase !== 'playing' || ftux.active === null) return;
    const step = ftux.view?.key ?? null;
    // The corner's way out is the FTUX's close, its X ("Close").
    const label = CLOSE_LABEL;
    const control = via;
    dispatch(ftuxEvent({ type: 'clicked', piece: ftux.active, step, control, label }));
    dispatch(phaseSet({ phase: 'none', active: null }));
  };

/**
 * The waiting piece checked against its availability while nothing is open and the gate is open (reconcileWaiting):
 * never ends it skipped, a deferred piece earlier in the script that is ready now takes its place (Spotlight's card
 * changes with no reload), not yet defers it when another piece can take its place, an unknown one moves the run on.
 * Dispatch it whenever the progress, the availability or the gate changes; a no-op otherwise.
 */
export const ftuxReconcile = (): FtuxThunk => (dispatch, getState) => {
  const state = getState();
  const { progress, phase, gate, shipped, availability } = state.ftux;
  if (!canWrite(state) || phase !== 'none' || gate?.canRun !== true || !shipped || progress?.status !== 'waiting') {
    return;
  }
  const ids = shipped.map((meta) => meta.id);
  const next = reconcileWaiting(progress, ids, availability);
  if (next) commit(dispatch, progress.status, next);
};

/* The QA strip */

/** Where a QA jump lands and the step its event names. */
const jumpOf = (target: FtuxQaTarget): { status: FtuxStatus; piece: BrandPieceId | null; step: FtuxFlowStep } => {
  if (target === 'hello') return { status: 'hello', piece: null, step: 'lumio-hello' };
  if (target === 'map') return { status: 'lap', piece: null, step: mapStep('numbers') };
  if (target === 'finished') return { status: 'finished', piece: null, step: 'finished' };
  return { status: 'waiting', piece: target, step: 'spotlight-pick' };
};

/**
 * The strip's pill (`label`, its English title), for the brand on screen only: Lumio's hello or the map replay the
 * intro, the brand starting again from its first piece after the map; a piece's card or the end start a new run of
 * the brand there. Other brands keep their runs.
 */
export const ftuxQaJump =
  (target: FtuxQaTarget, label: string): FtuxThunk =>
  (dispatch, getState) => {
    const state = getState();
    const { brandId } = state.ftux;
    if (!canWrite(state) || brandId === null) return;
    const jump = jumpOf(target);
    const replay = jump.status === 'hello' || jump.status === 'lap';
    // A jump to a piece or the end marks the intro done: written only when it was not done yet.
    const introMoves = !replay && state.ftux.intro?.status !== 'done';
    if (replay) {
      dispatch(savePending({ [runKeyOf(brandId)]: null }));
      dispatch(runDropped());
    }
    dispatch(progressSet(freshRun(state.ftux.progress, jump.status, jump.piece)));
    dispatch(persistFtux(replay ? INTRO : introMoves ? INTRO_AND_RUN : RUN));
    dispatch(ftuxEvent({ type: 'qa', piece: jump.piece, step: jump.step, control: 'jump', label }));
  };

/**
 * The strip's Restart (`label`, its English copy): the owner starts over, Lumio's hello first, then every brand
 * from its first piece (each brand's run this tab knows is dropped from the owner's preferences).
 */
export const ftuxQaRestart =
  (label: string): FtuxThunk =>
  (dispatch, getState) => {
    const state = getState();
    if (!canWrite(state)) return;
    const dropped: FtuxPatch = Object.fromEntries(Object.keys(state.ftux.runs).map((id) => [runKeyOf(id), null]));
    dispatch(savePending(dropped));
    dispatch(runsCleared());
    dispatch(progressSet(freshRun(state.ftux.progress, 'hello', null)));
    dispatch(persistFtux(INTRO));
    dispatch(ftuxEvent({ type: 'qa', piece: null, step: 'lumio-hello', control: 'restart', label }));
  };
